// In-browser renderers for a student's ORIGINAL files (SubmissionViewer raw
// mode). Each takes the file's bytes and fills a container element; nothing
// here reads the pipeline's extracted text or understanding. Documents that
// the pipeline marked "unsupported" (spreadsheets) use these after assessment
// as well, since there is no transcript to show instead.
import { renderAsync } from 'docx-preview';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';

const esc = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export function rendererFor(asset) {
  const name = (asset.original_name || '').toLowerCase();
  const mime = asset.mime || '';
  if (name.endsWith('.docx') || mime.includes('wordprocessingml')) return 'docx';
  if (name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.csv') || mime.includes('spreadsheetml') || mime.includes('ms-excel'))
    return 'xlsx';
  if (name.endsWith('.pptx') || mime.includes('presentationml')) return 'pptx';
  if (name.endsWith('.pdf') || mime.includes('pdf')) return 'pdf';
  return null;
}

export async function renderDocx(blob, container) {
  container.innerHTML = '';
  await renderAsync(blob, container, undefined, {
    inWrapper: true,
    ignoreWidth: false,
    breakPages: true,
    renderHeaders: true,
    renderFooters: true,
    useBase64URL: true,
  });
}

// Every sheet as a table, in workbook order. sheet_to_html keeps merged cells
// and the cell text as Excel would display it (formatted numbers, dates).
export function renderXlsx(arrayBuffer, container) {
  const wb = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
  const parts = wb.SheetNames.map((name) => {
    const html = XLSX.utils.sheet_to_html(wb.Sheets[name], { header: '', footer: '' });
    return `<section class="arcdisk-sheet"><h4 class="arcdisk-sheet-name">${esc(name)}</h4><div class="arcdisk-sheet-table">${html}</div></section>`;
  });
  container.innerHTML = parts.join('') || '<p class="arcdisk-sheet-empty">This workbook has no sheets.</p>';
}

// Slides as 16:9 cards: the deck's pictures and text runs in slide order.
// Reads the OOXML directly (presentation.xml → slide parts → their rels for
// media). Layout fidelity is approximate; the content is the student's own.
export async function renderPptx(arrayBuffer, container) {
  const zip = await JSZip.loadAsync(arrayBuffer);
  const xml = async (path) => {
    const f = zip.file(path);
    if (!f) return null;
    return new DOMParser().parseFromString(await f.async('text'), 'application/xml');
  };
  const relsOf = async (partPath) => {
    const dir = partPath.slice(0, partPath.lastIndexOf('/'));
    const file = partPath.slice(partPath.lastIndexOf('/') + 1);
    const doc = await xml(`${dir}/_rels/${file}.rels`);
    const map = new Map();
    if (!doc) return map;
    for (const rel of Array.from(doc.getElementsByTagName('Relationship'))) {
      const target = rel.getAttribute('Target') || '';
      const abs = target.startsWith('/') ? target.slice(1) : normalize(`${dir}/${target}`);
      map.set(rel.getAttribute('Id'), abs);
    }
    return map;
  };

  const pres = await xml('ppt/presentation.xml');
  const presRels = await relsOf('ppt/presentation.xml');
  const slidePaths = pres
    ? Array.from(pres.getElementsByTagNameNS('*', 'sldId'))
        .map((el) => presRels.get(el.getAttribute('r:id') || el.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'id')))
        .filter(Boolean)
    : Object.keys(zip.files)
        .filter((p) => /^ppt\/slides\/slide\d+\.xml$/.test(p))
        .sort((a, b) => Number(a.match(/\d+/)[0]) - Number(b.match(/\d+/)[0]));

  const cards = [];
  for (const [i, path] of slidePaths.entries()) {
    const doc = await xml(path);
    if (!doc) continue;
    const rels = await relsOf(path);
    const shapes = Array.from(doc.getElementsByTagNameNS('*', 'sp'));
    let title = '';
    const paragraphs = [];
    for (const sp of shapes) {
      const ph = sp.getElementsByTagNameNS('*', 'ph')[0];
      const phType = ph ? ph.getAttribute('type') || 'body' : null;
      const paras = Array.from(sp.getElementsByTagNameNS('*', 'p'))
        .map((p) =>
          Array.from(p.getElementsByTagNameNS('*', 't'))
            .map((t) => t.textContent)
            .join('')
            .trim()
        )
        .filter(Boolean);
      if (!paras.length) continue;
      if ((phType === 'title' || phType === 'ctrTitle') && !title) title = paras.join(' ');
      else paragraphs.push(...paras);
    }
    const pics = [];
    for (const pic of Array.from(doc.getElementsByTagNameNS('*', 'pic'))) {
      const blip = pic.getElementsByTagNameNS('*', 'blip')[0];
      const rid = blip && (blip.getAttribute('r:embed') || blip.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'embed'));
      const mediaPath = rid && rels.get(rid);
      const file = mediaPath && zip.file(mediaPath);
      if (!file) continue;
      const blob = await file.async('blob');
      pics.push(URL.createObjectURL(blob));
    }
    cards.push(
      `<article class="arcdisk-slide">` +
        `<div class="arcdisk-slide-no">Slide ${i + 1}</div>` +
        (title ? `<h4 class="arcdisk-slide-title">${esc(title)}</h4>` : '') +
        (paragraphs.length ? `<ul class="arcdisk-slide-text">${paragraphs.map((p) => `<li>${esc(p)}</li>`).join('')}</ul>` : '') +
        (pics.length ? `<div class="arcdisk-slide-pics">${pics.map((src) => `<img src="${src}" alt="" />`).join('')}</div>` : '') +
        `</article>`
    );
  }
  container.innerHTML = cards.join('') || '<p class="arcdisk-sheet-empty">No slides found in this file.</p>';
}

function normalize(p) {
  const out = [];
  for (const seg of p.split('/')) {
    if (seg === '..') out.pop();
    else if (seg && seg !== '.') out.push(seg);
  }
  return out.join('/');
}
